import instaIcon from "../../assets/photographer/insta.svg"
import whatsappIcon from "../../assets/photographer/whatsapp.svg"

interface PhotographerBarProps {
  vendor: any
  /**
   * "light"   → white name text with a drop shadow, for bars sitting over a
   *              cover image.
   * "inherit" → name uses the surrounding theme text color, for bars over a
   *              plain background.
   */
  tone?: "light" | "inherit"
}

/**
 * Full-width bar pinned to the top of a gallery hero: photographer logo + name
 * on the left, Instagram / WhatsApp icons on the right. The parent must be
 * positioned (relative) for the absolute placement to anchor correctly.
 */
const PhotographerBar = ({ vendor, tone = "inherit" }: PhotographerBarProps) => {
  if (!vendor?.name) return null

  const logo = vendor?.compressedLogoLink || vendor?.logoLink

  const openInstagram = () => {
    const instagram = vendor?.instagram
    if (!instagram) return
    const url = instagram.startsWith("http")
      ? instagram
      : `https://instagram.com/${instagram.replace("@", "")}`
    window.open(url, "_blank")
  }

  const openWhatsapp = () => {
    if (!vendor?.phoneNumber) return
    const num = vendor.phoneNumber.replace(/[^0-9]/g, "")
    window.open(`https://wa.me/${num}`, "_blank")
  }

  // Over a cover image the white content can vanish on light photos, so sit
  // each cluster on a translucent dark pill for guaranteed contrast.
  const chip =
    tone === "light"
      ? "rounded-full bg-black/30 backdrop-blur-sm px-3 py-1.5"
      : ""

  // Recolor the golden SVG icons: always white over an image; over a plain
  // background follow the theme text (black in light, white in dark).
  const iconTone =
    tone === "light" ? "brightness-0 invert" : "brightness-0 dark:invert"

  return (
    <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 px-4 py-3 md:px-6 md:py-4">
      {/* Logo + name — top left. The logo stays on a transparent background;
          only the name sits on the pill. */}
      <div className="flex items-center gap-2">
        {logo && (
          <img
            src={logo}
            alt=""
            className="object-contain h-8 w-8 md:h-10 md:w-10 drop-shadow-md"
          />
        )}
        <span
          className={`inline-flex items-center text-sm md:text-base ${chip} ${
            tone === "light" ? "text-white drop-shadow-md" : ""
          }`}
        >
          {vendor?.name}
        </span>
      </div>

      {/* Icons — top right */}
      {(vendor?.instagram || vendor?.phoneNumber) && (
        <div className={`flex items-center gap-3 ${chip}`}>
          {vendor?.instagram && (
            <img
              src={instaIcon}
              alt="Instagram"
              className={`h-6 w-6 cursor-pointer drop-shadow-md ${iconTone}`}
              onClick={openInstagram}
            />
          )}
          {vendor?.phoneNumber && (
            <img
              src={whatsappIcon}
              alt="WhatsApp"
              className={`h-6 w-6 cursor-pointer drop-shadow-md ${iconTone}`}
              onClick={openWhatsapp}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default PhotographerBar
